/* Phase 12 UAT (Feb 2026) — Portal Attendance task.
   Renders any interviews the candidate needs to confirm (scheduled datetime in
   the past, no attendance yet). No incentive amounts or approval info is ever
   shown here — this is purely the candidate's confirmation of what happened. */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const OUTCOME_OPTIONS = [
  { key: "Attended",    label: "Attended Interview" },
  { key: "Rescheduled", label: "Rescheduled" },
  { key: "Cancelled",   label: "Cancelled" },
  { key: "No Show",     label: "No Show" },
];

async function authFetch(url, opts = {}) {
  const token = sessionStorage.getItem("portal_token");
  const backend = process.env.REACT_APP_BACKEND_URL;
  const res = await fetch(`${backend}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    let msg = "Request failed";
    try { msg = (await res.json())?.detail || msg; } catch (_) {}
    throw new Error(msg);
  }
  return res.json();
}

function formatWhen(row) {
  const d = row.interview_date || "";
  const t = row.interview_time || "";
  return `${d}${t ? ` · ${t}` : ""}${row.timezone ? ` (${row.timezone})` : ""}`;
}

export default function PortalAttendance() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await authFetch("/api/portal/interviews/pending-attendance");
      setRows(d?.rows || []);
    } catch (ex) {
      toast.error(ex.message || "Failed to load attendance tasks");
      setRows([]);
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="portal-attendance-view" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Interview Attendance</h3>
        <span className="text-xs text-slate-500">
          {busy ? "Loading…" : `${rows.length} to confirm`}
        </span>
      </div>
      <p className="text-sm text-slate-500 max-w-2xl">
        Please confirm what happened for each interview scheduled in the past.
        Your responses go directly to your assigned team for record-keeping.
      </p>
      {!busy && rows.length === 0 && (
        <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500"
             data-testid="portal-attendance-empty">
          You&apos;re all caught up — no interviews need confirmation right now.
        </div>
      )}
      {rows.map((row) => (
        <AttendanceRow
          key={row.id}
          row={row}
          expanded={expanded === row.id}
          onExpand={() => setExpanded(expanded === row.id ? null : row.id)}
          submitting={submitting}
          setSubmitting={setSubmitting}
          onSubmitted={load}
        />
      ))}
    </div>
  );
}

function AttendanceRow({ row, expanded, onExpand, submitting, setSubmitting, onSubmitted }) {
  const [outcome, setOutcome] = useState("");
  const [feedback, setFeedback] = useState("");
  const [q, setQ] = useState(["", "", "", "", ""]);
  const [comments, setComments] = useState("");
  const [declaration, setDeclaration] = useState(false);

  const submit = async () => {
    if (!outcome) { toast.error("Select what happened for this interview."); return; }
    if (outcome === "Attended") {
      if (!declaration) { toast.error("Please confirm the attendance declaration."); return; }
      if (!feedback.trim()) { toast.error("Add interview feedback or a short summary."); return; }
      const qs = q.map((x) => (x || "").trim()).filter(Boolean);
      if (qs.length === 0) { toast.error("Share at least one question you were asked."); return; }
    }
    setSubmitting(true);
    try {
      await authFetch(`/api/portal/interviews/${row.id}/attendance`, {
        method: "POST",
        body: JSON.stringify({
          outcome,
          feedback: outcome === "Attended" ? feedback : null,
          top_questions: outcome === "Attended" ? q.map((x) => (x || "").trim()).filter(Boolean) : null,
          candidate_comments: comments || null,
          declaration_confirmed: outcome === "Attended" ? declaration : true,
        }),
      });
      toast.success("Attendance confirmed. Thank you!");
      onSubmitted();
    } catch (ex) {
      toast.error(ex.message || "Failed to submit attendance");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg"
         data-testid={`attendance-row-${row.id}`}>
      <button onClick={onExpand}
              data-testid={`attendance-row-toggle-${row.id}`}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition">
        <div className="text-left">
          <div className="font-medium text-slate-800">
            {row.company || "—"} · <span className="text-slate-500">{row.job_title || "—"}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {formatWhen(row)} · Round {row.round || "—"} · {row.interview_type || "Video"}
          </div>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
          Confirm attendance
        </span>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-4" data-testid={`attendance-form-${row.id}`}>
          {/* Prefilled read-only fields */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <ReadOnly label="Date & Time" value={formatWhen(row)} testid={`attendance-when-${row.id}`} />
            <ReadOnly label="Company" value={row.company || "—"} testid={`attendance-company-${row.id}`} />
            <ReadOnly label="Role / Requirement" value={row.job_title || "—"} testid={`attendance-job-${row.id}`} />
            <ReadOnly label="Round" value={row.round || "—"} testid={`attendance-round-${row.id}`} />
          </div>

          {/* Outcome */}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">What happened? <span className="text-red-500">*</span></div>
            <div className="grid grid-cols-2 gap-2">
              {OUTCOME_OPTIONS.map((o) => (
                <label key={o.key}
                       className={`text-sm border rounded-md px-3 py-2 cursor-pointer transition
                                   ${outcome === o.key
                                     ? "bg-sky-50 border-sky-300 text-sky-800"
                                     : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}
                       data-testid={`attendance-outcome-${row.id}-${o.key.replace(/\s+/g, "-")}`}>
                  <input type="radio" name={`outcome-${row.id}`} className="mr-2"
                         value={o.key} checked={outcome === o.key}
                         onChange={(e) => setOutcome(e.target.value)} />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          {outcome === "Attended" && (
            <>
              <TextArea label="Interview Feedback / Summary" value={feedback} onChange={setFeedback}
                        required rows={3} testid={`attendance-feedback-${row.id}`} />
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5">
                  Top 5 questions asked during the interview <span className="text-red-500">*</span>
                </div>
                <div className="space-y-2">
                  {q.map((val, i) => (
                    <input key={`q-${row.id}-${i}`} value={val}
                           onChange={(e) => setQ((arr) => arr.map((v, idx) => idx === i ? e.target.value : v))}
                           placeholder={`Question ${i + 1}`}
                           data-testid={`attendance-question-${row.id}-${i}`}
                           className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                  ))}
                </div>
              </div>
            </>
          )}

          <TextArea label="Additional comments (optional)" value={comments} onChange={setComments} rows={2}
                    testid={`attendance-comments-${row.id}`} />

          {outcome === "Attended" && (
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer"
                   data-testid={`attendance-declaration-${row.id}`}>
              <input type="checkbox" className="mt-1"
                     checked={declaration}
                     onChange={(e) => setDeclaration(e.target.checked)} />
              <span>
                I confirm that I attended this interview on <b>{row.interview_date}</b>
                {row.interview_time && <> at <b>{row.interview_time} {row.timezone || ""}</b></>}.
                I understand this declaration is timestamped and preserved in my record.
              </span>
            </label>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={submit} disabled={submitting}
                    data-testid={`attendance-submit-${row.id}`}
                    className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50">
              {submitting ? "Submitting…" : "Submit confirmation"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReadOnly({ label, value, testid }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-400 font-medium">{label}</div>
      <div className="text-slate-800 mt-0.5" data-testid={testid}>{value}</div>
    </div>
  );
}

function TextArea({ label, value, onChange, rows, required, testid }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </div>
      <textarea rows={rows || 3} value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
    </div>
  );
}
